import { LightningBoltIcon } from "@radix-ui/react-icons";
import { TaskResponse, TaskUpdateResponse } from "client/openapi";
import { Dialog, DialogContent } from "components/Dialog";
import { errorText } from "components/Forms/formsApi";
import { useCallback, useContext, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import Notifications from "util/notifications";
import { OrgRolesAndAccountContext } from "util/OrgRolesAccountContext";
import { automationHref } from "./ActivityTabParts";
import ApprovalCards from "./ApprovalCards";
import {
  AutomationsProvider,
  sharedAutomations,
  useApprovals,
  useViewer,
} from "./hooks";
import type { ApprovalItem } from "./types";

/*
 * Automations on the app's own Tasks (the dashboard's Tasks view, the
 * Manage Tasks dialog and a task's window). A status change goes through
 * the app's own API as always, and its answer carries the Ask first items
 * it made that the person who changed it can approve (next_up): they pop
 * up in Next up. A tutor's waiting items show at the top of their Tasks.
 * The tasks automations made carry an Automatic chip.
 */

/** The automation that made a task. */
export interface TaskAutomation {
  id: string;
  name: string;
  deleted: boolean;
}

/** The automation that made a task, if one did (the task's own automation). */
export function automationOfTask(task: TaskResponse): TaskAutomation | null {
  const made = task.automation;
  if (!made) return null;
  if (made.deleted || made.id === null || made.id === undefined)
    return { id: "", name: made.name ?? "", deleted: true };
  return { id: String(made.id), name: made.name ?? "", deleted: false };
}

/**
 * What the Tasks pieces need from Automations: after a status change, the
 * items its answer made that pop up for the signed-in person.
 */
export function useTaskAutomations() {
  const { currently_selected_organization: orgId } = useContext(
    OrgRolesAndAccountContext
  );
  const viewer = useViewer();

  /**
   * A task's status changed through the app's own API (`after` is its
   * answer): the items that pop up for this person.
   */
  const changedInTadpole = useCallback(
    async (before: TaskResponse, after: TaskUpdateResponse) => {
      if (before.status === after.status) return [];
      const found = orgId
        ? await sharedAutomations(orgId, viewer).catch(() => null)
        : null;
      if (!found) return [];
      try {
        return await found.api.afterTaskChange({
          response: after,
          from: before.status,
        });
      } catch (e) {
        Notifications.error(
          errorText(e, "Automations could not check this change.")
        );
        return [];
      }
    },
    [orgId, viewer]
  );

  return { changedInTadpole };
}

/** A small chip on a task an automation made, opening that automation (for admins). */
export function AutomaticChip({ automation }: { automation: TaskAutomation }) {
  const viewer = useViewer();
  const icon = (
    <LightningBoltIcon
      className="-mt-px mr-0.5 inline h-2.5 w-2.5"
      aria-hidden
    />
  );
  const tip = automation.deleted ? undefined : `Made by ${automation.name}`;
  if (automation.deleted || viewer.tutor)
    return (
      <span
        className={`person-pill person-pill--${
          automation.deleted ? "gray" : "purple"
        }`}
        title={tip}
      >
        {icon}
        Automatic
      </span>
    );
  return (
    <Link
      to={automationHref(automation.id)}
      className="person-pill person-pill--purple person-pill--button"
      title={tip}
      onClick={(e) => e.stopPropagation()}
    >
      {icon}
      Automatic
    </Link>
  );
}

/** The items that just popped up, while they still wait; onEmpty once none does. */
function NextUpCards({ ids, onEmpty }: { ids: string[]; onEmpty: () => void }) {
  const { approvals, loading } = useApprovals({ mine: true });
  const open = approvals.filter((item) => ids.includes(item.id));
  const empty = !loading && open.length === 0;
  const done = useRef(onEmpty);
  done.current = onEmpty;
  useEffect(() => {
    if (empty) done.current();
  }, [empty]);
  if (loading) return null;
  return <ApprovalCards approvals={open} wide={false} />;
}

/**
 * Next up: the items a status change made that pop up for this person,
 * over the page, each answered in place. Closing it leaves them waiting
 * for approval.
 */
export function NextUp({
  orgId,
  items,
  onClose,
}: {
  orgId: number | null | undefined;
  items: ApprovalItem[];
  onClose: () => void;
}) {
  if (!orgId || !items.length) return null;
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        className="dialog-content max-w-[560px]"
        aria-label="Next up"
      >
        <h2 className="mb-3 text-xl font-semibold">Next up</h2>
        <AutomationsProvider orgId={orgId} loading={null} failed={() => null}>
          <NextUpCards ids={items.map((item) => item.id)} onEmpty={onClose} />
        </AutomationsProvider>
      </DialogContent>
    </Dialog>
  );
}

/** A tutor's items waiting for approval, at the top of their Tasks. */
function TutorWaiting() {
  const { approvals } = useApprovals({ mine: true });
  if (!approvals.length) return null;
  return (
    <section className="mb-6" aria-labelledby="tasks-waiting-for-approval">
      <h3
        id="tasks-waiting-for-approval"
        className="mb-3 text-base font-bold text-neutral-900"
      >
        Waiting for approval
      </h3>
      <ApprovalCards approvals={approvals} />
    </section>
  );
}

/** At the top of a tutor's Tasks: the items waiting for them. Nothing for anyone else. */
export function TasksWaiting({
  orgId,
  tutor,
}: {
  orgId: number | null | undefined;
  tutor: boolean;
}) {
  if (!orgId || !tutor) return null;
  return (
    <AutomationsProvider orgId={orgId} loading={null} failed={() => null}>
      <TutorWaiting />
    </AutomationsProvider>
  );
}
